/**
 * 网络监测插件
 * 提供网络连接状态监测和提示功能
 */

export default {
    /**
     * 安装网络监测插件
     * @param {Vue} Vue - Vue实例
     */
    install(Vue) {
        // 创建网络监测方法
        const networkMonitor = {
            // 网络不稳定提示的节流时间 (单位: ms)
            unstableNoticeThrottle: 60000, // 60秒内最多提示一次
            // 上次网络不稳定提示时间
            lastUnstableNotice: 0,
            // Vue实例引用
            vueInstance: null,
            // 离线提示的消息实例
            offlineMessageInstance: null,
            // 在线提示的消息实例
            onlineMessageInstance: null,
            // 网络慢提示的消息实例
            slowNetworkMessageInstance: null,

            /**
             * 初始化网络监测
             * @param {Vue} vm - Vue实例
             */
            init(vm) {
                // 保存Vue实例引用
                this.vueInstance = vm;

                // 检查初始网络状态
                this.checkNetworkStatus(vm);

                // 监听网络连接状态变化
                window.addEventListener('online', () => {
                    this.handleOnline(vm);
                });

                window.addEventListener('offline', () => {
                    this.handleOffline(vm);
                });

                // 监听网络连接信息变化
                const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                if (connection) {
                    connection.addEventListener('change', () => {
                        this.handleConnectionChange(vm);
                    });
                }
            },

            /**
             * 检查网络状态
             * @param {Vue} vm - Vue实例
             */
            checkNetworkStatus(vm) {
                if (!navigator.onLine) {
                    this.showOfflineMessage(vm);
                } else {
                    // 网络在线，检查连接质量
                    this.checkConnectionQuality(vm);
                }
            },

            /**
             * 检查连接质量
             * @param {Vue} vm - Vue实例
             */
            checkConnectionQuality(vm) {
                const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                if (connection) {
                    // console.log('[网络监测] 网络类型：', connection.effectiveType);
                    // console.log('[网络监测] 下载速度：', connection.downlink, 'Mbps');
                    // console.log('[网络监测] 往返时间：', connection.rtt, 'ms');
                    // console.log('[网络监测] 数据保存模式：', connection.saveData ? '开启' : '关闭');

                    // 如果下载速度低于0.4 Mbps (约50 KB/s) 或往返时间大于1000ms，视为网络不稳定
                    if (connection.downlink < 0.4 || connection.rtt > 1000) {
                        this.showUnstableNetworkMessage(vm);
                    }
                }
            },

            /**
             * 处理网络连接变化
             * @param {Vue} vm - Vue实例
             */
            handleConnectionChange(vm) {
                // 只有在网络在线时才检查连接质量
                if (navigator.onLine) {
                    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                    if (connection) {
                        // console.log('[网络监测] 网络连接信息变化');
                        // console.log('[网络监测] 网络类型：', connection.effectiveType);
                        // console.log('[网络监测] 下载速度：', connection.downlink, 'Mbps');
                        // console.log('[网络监测] 往返时间：', connection.rtt, 'ms');

                        // 根据连接信息判断网络质量
                        if (connection.downlink < 0.4 || connection.rtt > 1000) {
                            this.showUnstableNetworkMessage(vm);
                        }
                    }
                }
            },

            /**
             * 处理在线状态
             * @param {Vue} vm - Vue实例
             */
            handleOnline(vm) {
                // 检查连接质量后再决定显示什么消息
                this.checkConnectionQualityAndShowMessage(vm);
            },

            /**
             * 检查连接质量并显示适当的消息
             * @param {Vue} vm - Vue实例
             */
            checkConnectionQualityAndShowMessage(vm) {
                const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                let isNetworkSlow = false;

                if (connection) {
                    isNetworkSlow = connection.downlink < 0.4 || connection.rtt > 1000;
                }

                // 关闭离线消息并清空引用
                this.closeAndClearMessageInstance('offlineMessageInstance');

                if (isNetworkSlow) {
                    // 网络慢，显示网络慢提示
                    this.showUnstableNetworkMessage(vm);
                } else {
                    // 网络正常，显示网络恢复提示
                    this.showOnlineMessage(vm);
                }
            },

            /**
             * 处理离线状态
             * @param {Vue} vm - Vue实例
             */
            handleOffline(vm) {
                // console.log('[网络监测] 网络已断开');
                this.showOfflineMessage(vm);
            },

            /**
             * 安全地关闭消息实例
             * @param {*} messageInstance - 消息实例
             */
            closeMessageInstance(messageInstance) {
                // 使用官方推荐的 destroy 方法
                if (messageInstance) {
                    try {
                        messageInstance.close();
                        return true;
                    } catch (e) {
                        console.error('[网络监测] 使用 destroy() 关闭消息实例时出错:', e);
                        return false;
                    }
                }
                return false;
            },

            /**
             * 关闭并清空消息实例
             * @param {string} instanceName - 实例名称
             */
            closeAndClearMessageInstance(instanceName) {
                if (this[instanceName]) {
                    this.closeMessageInstance(this[instanceName]);
                    this[instanceName] = null;
                }
            },

            /**
             * 显示离线消息
             * @param {Vue} vm - Vue实例
             */
            showOfflineMessage(vm) {
                // 关闭之前可能存在的在线消息和网络慢消息
                this.closeAndClearMessageInstance('onlineMessageInstance');
                this.closeAndClearMessageInstance('slowNetworkMessageInstance');

                // 显示离线消息并保存实例引用
                this.offlineMessageInstance = vm.$message.error({
                    message: vm.$t('common.networkUnstable'),
                    duration: 0,
                    showClose: true,
                    background: true
                });
            },

            /**
             * 显示在线消息
             * @param {Vue} vm - Vue实例
             */
            showOnlineMessage(vm) {
                // 关闭网络慢消息
                this.closeAndClearMessageInstance('slowNetworkMessageInstance');
                this.closeAndClearMessageInstance('offlineMessageInstance');

                // 显示网络恢复消息
                this.onlineMessageInstance = vm.$message.success({
                    message: vm.$t('common.networkFull'),
                    duration: 3000,
                    background: true
                });
            },

            /**
             * 显示网络不稳定消息
             * @param {Vue} vm - Vue实例
             */
            showUnstableNetworkMessage(vm) {
                const now = Date.now();
                // 节流处理，避免频繁提示
                if (now - this.lastUnstableNotice > this.unstableNoticeThrottle) {
                    this.lastUnstableNotice = now;

                    // 关闭可能存在的网络恢复提示，避免重复
                    this.closeAndClearMessageInstance('onlineMessageInstance');
                    this.closeAndClearMessageInstance('offlineMessageInstance');

                    // 显示网络慢消息并保存实例引用
                    this.slowNetworkMessageInstance = vm.$message.warning({
                        message: vm.$t('common.networkSlow'),
                        duration: 5000,
                        showClose: true,
                        background: true
                    });
                }
            }
        };

        // 将网络监测功能添加到Vue原型链上
        Vue.prototype.$networkMonitor = networkMonitor;
    }
};
